import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Label, Message, Table } from 'semantic-ui-react';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import useEntityTaskExecutions from '../../../../../hooks/task/useEntityTaskExecutions';
import useCompanies from '../../../../../hooks/useCompanies';
import useFullModalState from '../../../../../hooks/useFullModalState';
import useModalState from '../../../../../hooks/useModalState';
import useUsers from '../../../../../hooks/useUsers';
import {
    TaskCreationSchemaType,
    TaskExecutionDetailsType,
    TaskExecutionUpdateSchemaType,
    TaskStatusType,
    TaskUpdateSchemaType,
} from '../../../../../schemas/task.schemas';
import Alert from '../../../../../utils/alert';
import TasksRepo from '../../../../../utils/repository/tasksRepo/tasksRepo';
import Pagination from '../../../../pagination';
import TaskDueDate from '../../taskDueDate';
import TaskExecutionUpdateModal from '../../taskExecutionUpdateModal';
import TaskModal from '../../taskModal';
import { getActionOption } from '../../utils';
import Loader from '../../../../loader';

type EntityTaskExecutionsTableProps = {
    entityId: number;
    statuses: TaskStatusType[];
    showTableActions?: boolean;
};

function EntityTaskExecutionsTable({ entityId, statuses, showTableActions = false }: EntityTaskExecutionsTableProps) {
    const { entityExecutions, refetch, page, setPage, totalPages, loading } = useEntityTaskExecutions(
        entityId,
        statuses
    );
    const { companies } = useCompanies();
    const { users } = useUsers();
    const taskCreateModalState = useModalState();
    const { open, closeModal, openModal, modalData } = useFullModalState<TaskExecutionDetailsType>();
    const navigate = useNavigate();

    const navigateToTask = (taskId: number) => {
        navigate(`/admin/tasks/${taskId}`);
    };

    const handleCreateTaskWithEntity = (data: TaskCreationSchemaType & TaskUpdateSchemaType) => {
        const taskData: TaskCreationSchemaType = {
            assignedUserId: data.assignedUserId,
            createdById: data.createdById,
            description: data.description,
            dueDate: data.dueDate,
            priority: data.priority,
            title: data.title,
        };

        TasksRepo.createTask(taskData, (taskResponse) => {
            if (taskResponse.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Task Created Successfully!');
                TasksRepo.addTaskExecutions(
                    taskResponse.data.id,
                    {
                        taskId: taskResponse.data.id,
                        entities: [
                            {
                                entityId: entityId,
                                entityType: 'Company',
                            },
                        ],
                    },
                    (executionResponse) => {
                        if (executionResponse.status === RESPONSE_STATUS.SUCCESS) {
                            Alert.success('Execution created successfully!');
                            refetch();
                        } else {
                            Alert.warn(executionResponse.message);
                        }
                    }
                );
            } else {
                Alert.warn(taskResponse.message);
            }
        });
        taskCreateModalState.closeModal();
    };

    const handleUpdateTaskExecution = (execution: TaskExecutionUpdateSchemaType) => {
        TasksRepo.updateTaskExecution(execution.taskId, execution, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Execution updated successfully!');
                closeModal();
                refetch();
            } else {
                Alert.warn(response.message);
            }
        });
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {entityExecutions.length ? (
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Task Id</Table.HeaderCell>
                            <Table.HeaderCell>Task Title</Table.HeaderCell>
                            <Table.HeaderCell>Priority</Table.HeaderCell>
                            <Table.HeaderCell>Execution Status</Table.HeaderCell>
                            <Table.HeaderCell>Due Date</Table.HeaderCell>
                            <Table.HeaderCell collapsing></Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {entityExecutions.map((e) => {
                            return (
                                <Table.Row className="cursorPointer" onDoubleClick={() => openModal(e)} key={e.id}>
                                    <Table.Cell>
                                        <Label>{e.taskId}</Label>
                                    </Table.Cell>
                                    <Table.Cell>{e.taskTitle}</Table.Cell>
                                    <Table.Cell>{e.taskPriority}</Table.Cell>
                                    <Table.Cell>{e.status}</Table.Cell>
                                    <Table.Cell>
                                        <TaskDueDate date={e.dueDate} status={e.status} />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Dropdown
                                            name="actions"
                                            icon="ellipsis vertical"
                                            options={[
                                                getActionOption('openTask', 'Open Task', () =>
                                                    navigateToTask(e.taskId)
                                                ),
                                            ].map((option) => ({
                                                ...option,
                                                onClick: () => option.onClick(e),
                                            }))}
                                            trigger={<></>}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            ) : (
                <Message>There are currently no tasks</Message>
            )}

            <div className="flex full-width">
                {!!entityExecutions.length && <Pagination page={page} totalPages={totalPages} setPage={setPage} />}
                {showTableActions && (
                    <Button className="ml-auto" onClick={taskCreateModalState.openModal}>
                        Create Task
                    </Button>
                )}
            </div>
            <TaskModal
                onCancel={taskCreateModalState.closeModal}
                onSave={handleCreateTaskWithEntity}
                open={taskCreateModalState.open}
                users={users}
            />
            <TaskExecutionUpdateModal
                onCancel={closeModal}
                open={open}
                companies={companies}
                execution={modalData}
                onSave={handleUpdateTaskExecution}
            />
        </>
    );
}

export default EntityTaskExecutionsTable;
